import { SelectItem } from "../types/selectFieldInternalTypes";
import Spinner from "../../components/Spinner";
import Button from "../../components/Button";
import styles from "./SelectOptions.module.scss";
import cx from "classnames";
import {
  CSSProperties,
  forwardRef,
  ReactNode,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { SelectOption } from "./SelectOption";
import { InView } from "./InView";
import { Text } from "../../components/Text";
import { SrOnly } from "../../components";
import Divider from "../../components/Divider";
import { CheckState } from "../../types";
import {
  GetItemPropsFn,
  GetMenuPropsFn,
  SectionMeta,
} from "../types/selectFieldInternalTypes";
import { useMergeRefs } from "../../hooks/useMergeRefs";
import { useOptionsSections } from "../hooks/useOptionsSections";

export type OptionsPanelHandle = {
  scrollToTop: () => void;
  scrollToIndex: (index: number) => void;
};

export type OptionsPanelProps = {
  isOpen: boolean;
  items: SelectItem[];
  getMenuProps: GetMenuPropsFn;
  getItemProps: GetItemPropsFn;
  highlightedIndex: number;
  getCheckedState: (item: SelectItem) => CheckState;
  selectionType: "single" | "multiple";
  hasMore: boolean;
  onLoadMore: () => void;
  loading: boolean;
  loadingMore: boolean;
  disableAutoLoadMore?: boolean;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  sectionsMeta?: SectionMeta[];
};

export const OptionsPanel = forwardRef<OptionsPanelHandle, OptionsPanelProps>(
  (
    {
      isOpen,
      items,
      getMenuProps,
      getItemProps,
      highlightedIndex,
      getCheckedState,
      selectionType,
      hasMore,
      onLoadMore,
      loading,
      loadingMore,
      disableAutoLoadMore,
      disabled,
      className,
      style,
      sectionsMeta = [],
    },
    ref,
  ) => {
    const baseId = useId();
    const scrollSentinelRef = useRef<HTMLDivElement>(null);
    const scrollerRef = useRef<HTMLDivElement>(null);
    const [isScrolled, setIsScrolled] = useState(false);

    useImperativeHandle(ref, () => ({
      scrollToTop: () => {
        if (scrollerRef.current) {
          scrollerRef.current.scrollTop = 0;
        }
      },
      scrollToIndex: (index: number) => {
        if (!scrollerRef.current) return;
        if (index <= 0) {
          // special case for first item allows group header to be visible
          scrollerRef.current.scrollTop = 0;
          return;
        }
        const item = scrollerRef.current.querySelector(
          `[data-item-index="${index}"]`,
        );
        if (item) {
          item.scrollIntoView({ block: "nearest" });
        }
      },
    }));

    // IntersectionObserver to detect scroll state for sticky shadow.
    useEffect(() => {
      if (!isOpen) {
        setIsScrolled(false);
        return;
      }
      if (!scrollSentinelRef.current) return;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            setIsScrolled(!entry.isIntersecting);
          });
        },
        { threshold: 0, root: scrollerRef.current },
      );
      observer.observe(scrollSentinelRef.current);
      return () => observer.disconnect();
    }, [isOpen]);

    // Check if any pinned section is still loading its options.
    // If so, show the main list loading indicator to avoid layout shift.
    const anyPinnedLoading = sectionsMeta.some(
      (s) => s.type === "pinned" && s.loading,
    );

    const handleLoaderVisibilityChange = (visible: boolean) => {
      if (visible && !disableAutoLoadMore) {
        onLoadMore();
      }
    };

    const sections = useOptionsSections(items, sectionsMeta);

    const renderItem = (item: SelectItem, index: number): ReactNode => (
      <SelectOption
        key={item.id}
        item={item}
        index={index}
        getItemProps={getItemProps}
        selectionType={selectionType}
        checked={getCheckedState(item)}
        highlighted={highlightedIndex === index}
        disabled={(disabled || item.disabled) ?? false}
        isListScrolled={isScrolled}
      />
    );

    const renderSections = (): ReactNode[] => {
      const result: ReactNode[] = [];

      sections.forEach((section, sectionIndex) => {
        const isLastSection = sectionIndex === sections.length - 1;

        if (section.type === "pinned" || section.type === "group") {
          const labelId = `${baseId}-section-${sectionIndex}`;

          result.push(
            <div
              key={`section-${sectionIndex}`}
              role="group"
              aria-labelledby={labelId}
              className={styles["pinned-section"]}
            >
              <span id={labelId} className={styles["pinned-section-header"]}>
                <Text variant="eyebrow">{section.label}</Text>
              </span>
              <div className={styles["pinned-section-list"]}>
                {section.items.map(({ item, index }) =>
                  renderItem(item, index),
                )}
              </div>
            </div>,
          );
          if (!isLastSection) {
            result.push(
              // Decorative — group boundaries are already conveyed by role="group" with aria-labelledby
              <div
                key={`divider-${sectionIndex}`}
                role="presentation"
                aria-hidden="true"
                className={styles["divider"]}
              >
                <Divider />
              </div>,
            );
          }
        } else {
          // Flat items - render directly
          section.items.forEach(({ item, index }) => {
            result.push(renderItem(item, index));
          });
        }
      });

      return result;
    };

    const menuProps = getMenuProps({
      className: cx(styles["scroller"], className),
      style,
    });

    const menuRef = useMergeRefs([menuProps.ref, scrollerRef]);

    return (
      <div {...menuProps} ref={menuRef}>
        <div className={styles["select-field-list"]}>
          {isOpen && (
            <div
              ref={scrollSentinelRef}
              className={styles["scroll-sentinel"]}
              role="presentation"
              aria-hidden="true"
            />
          )}
          {isOpen && (
            <>
              {loading || anyPinnedLoading ? (
                <div role="presentation" className={styles["empty"]}>
                  <Spinner size="medium" />
                </div>
              ) : items.length > 0 ? (
                renderSections()
              ) : (
                <div role="presentation" className={styles["empty"]}>
                  <Text subdued size="small">
                    No match found
                  </Text>
                </div>
              )}
            </>
          )}
          {loadingMore ? (
            <div role="presentation" className={styles["load-more"]}>
              <Spinner size="small" />
              <SrOnly>Loading more...</SrOnly>
            </div>
          ) : hasMore && !loading ? (
            <div role="presentation">
              <InView
                onVisibilityChange={handleLoaderVisibilityChange}
                rootMargin="0px 0px -100px 0px"
              >
                <div className={styles["load-more"]}>
                  <Button
                    appearance="secondary"
                    size="small"
                    onClick={onLoadMore}
                  >
                    Load more
                  </Button>
                </div>
              </InView>
            </div>
          ) : null}
        </div>
      </div>
    );
  },
);

OptionsPanel.displayName = "OptionsPanel";
